import React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import EnhancedStyleGuidelinesView from "../../ShowStylePage/components/EnhancedStyleGuidelinesView";
import { StylePageContent } from "../../ShowStylePage/StylePageContent";

export type PreviewView = "participant" | "judge";

export interface PreviewApiClient {
  scoresheetSchemas: {
    useFindScoresheetSchema?: (params: any, options?: any) => { data: any };
  };
}

interface StylePreviewModalProps {
  view: PreviewView | null;
  onClose: () => void;
  style: any;
  scoresheetSchemaId?: string;
  useApiClient: () => PreviewApiClient;
}

const noSchema = () => ({ data: undefined });

// Rendered only while the modal is open, so the schema is fetched only then.
const PreviewBody: React.FC<Omit<StylePreviewModalProps, "onClose">> = ({
  view,
  style,
  scoresheetSchemaId,
  useApiClient,
}) => {
  const api = useApiClient();
  const useFindSchema = api.scoresheetSchemas.useFindScoresheetSchema ?? noSchema;
  const { data: schemaData } = useFindSchema(
    { filter: { id: scoresheetSchemaId } },
    { enabled: Boolean(scoresheetSchemaId) },
  );

  return view === "judge" ? (
    <EnhancedStyleGuidelinesView style={style} />
  ) : (
    <StylePageContent style={style} schemaData={schemaData} />
  );
};

/**
 * Shows the style being edited the way participants (style page) or judges (guidelines
 * drawer in the scoresheet) will see it, before it is saved.
 */
export const StylePreviewModal: React.FC<StylePreviewModalProps> = ({ view, onClose, ...rest }) => {
  const { t } = useTranslation();

  const title =
    view === "judge"
      ? t("Pages:StyleCreator.Preview.JudgeTitle", "Judge view")
      : t("Pages:StyleCreator.Preview.ParticipantTitle", "Participant view");

  return (
    <Modal
      opened={view !== null}
      onClose={onClose}
      title={title}
      className="max-w-6xl !p-0 sm:!p-0"
    >
      <div>
        <div className="sticky top-0 z-40 flex items-center justify-between gap-4 rounded-t-2xl border-b border-[#dac2b6] bg-[#fff8f5] px-6 py-3">
          <div>
            <div className="text-sm font-bold text-[#6c2f00]">{title}</div>
            <div className="text-xs text-[#54433a]">
              {t(
                "Pages:StyleCreator.Preview.Unsaved",
                "Preview of the current, not yet saved form values.",
              )}
            </div>
          </div>
          <Button
            htmlType="button"
            type="base"
            variant="outlined"
            onClick={onClose}
            className="rounded-lg border-[#dac2b6] px-4 py-2 text-xs font-bold text-[#6c2f00]"
          >
            {t("Pages:StyleCreator.Preview.Close", "Close preview")}
          </Button>
        </div>
        {view !== null && <PreviewBody view={view} style={rest.style} scoresheetSchemaId={rest.scoresheetSchemaId} useApiClient={rest.useApiClient} />}
      </div>
    </Modal>
  );
};
